import { accountRewards, claimAccountReward } from './sogni.js';

const MONTHLY_BOOST_ID = '2';
const TURNSTILE_SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let trigger;
let modal;
let amountEl;
let errorEl;
let claimBtn;
let turnstileHost;
let siteKey;
let onClaimed;
let reward = null;
let widget = null;
let scriptPromise = null;
let refreshSequence = 0;

export function claimableMonthlyBoost(rewards, now = Date.now()) {
  const boost = Array.isArray(rewards)
    ? rewards.find((item) => String(item?.id) === MONTHLY_BOOST_ID)
    : null;
  if (!boost?.canClaim || boost.cantClaimReason) return null;
  if (boost.nextClaim && new Date(boost.nextClaim).getTime() > now) return null;
  return boost;
}

export function shouldSuppressMonthlyBoostReminder(subscriptionStatus) {
  return subscriptionStatus === 'active' || subscriptionStatus === 'trialing';
}

export function initMonthlyBoost({ turnstileSiteKey, claimed }) {
  trigger = document.getElementById('claim-boost');
  modal = document.getElementById('boost-modal');
  amountEl = document.getElementById('boost-amount');
  errorEl = document.getElementById('boost-error');
  claimBtn = document.getElementById('boost-claim');
  turnstileHost = document.getElementById('boost-turnstile');
  siteKey = turnstileSiteKey;
  onClaimed = claimed;

  trigger.onclick = openMonthlyBoost;
  claimBtn.onclick = showChallenge;
  document.getElementById('boost-close').onclick = close;
  document.getElementById('boost-dismiss').onclick = close;
  modal.addEventListener('click', (event) => {
    if (event.target === modal) close();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !modal.classList.contains('hidden')) close();
  });
}

export async function refreshMonthlyBoost(subscriptionStatus = null) {
  const sequence = ++refreshSequence;
  let nextReward = null;
  if (
    subscriptionStatus !== null &&
    subscriptionStatus !== undefined &&
    !shouldSuppressMonthlyBoostReminder(subscriptionStatus)
  ) {
    try {
      nextReward = claimableMonthlyBoost(await accountRewards());
    } catch {
      /* unavailable rewards fail closed */
    }
  }
  if (sequence !== refreshSequence) return null;
  reward = nextReward;
  trigger?.classList.toggle('hidden', !reward);
  if (!reward && modal) close();
  return reward;
}

export function clearMonthlyBoost() {
  refreshSequence++;
  reward = null;
  trigger?.classList.add('hidden');
  close();
}

export function openMonthlyBoost() {
  if (!reward) return;
  clearError();
  amountEl.textContent = Number(reward.amount).toLocaleString();
  claimBtn.classList.remove('hidden');
  turnstileHost.classList.add('hidden');
  modal.classList.remove('hidden');
}

function close() {
  removeWidget();
  modal.classList.add('hidden');
}

async function loadTurnstile() {
  if (window.turnstile) return;
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = TURNSTILE_SCRIPT;
      script.async = true;
      script.defer = true;
      script.onload = resolve;
      script.onerror = () => reject(new Error('The security check could not load.'));
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

async function showChallenge() {
  clearError();
  claimBtn.classList.add('hidden');
  turnstileHost.classList.remove('hidden');
  turnstileHost.innerHTML =
    '<span class="auth-spinner" aria-label="Loading security check"></span>';
  try {
    await loadTurnstile();
    turnstileHost.innerHTML = '';
    widget = window.turnstile.render(turnstileHost, {
      sitekey: siteKey,
      theme: 'dark',
      callback: claim,
      'error-callback': () => showError('The security check failed. Please try again.'),
      'expired-callback': () => showError('The security check expired. Please complete it again.')
    });
  } catch (error) {
    showError(error.message);
    claimBtn.classList.remove('hidden');
    turnstileHost.classList.add('hidden');
    scriptPromise = null;
  }
}

async function claim(token) {
  if (!reward) return;
  clearError();
  turnstileHost.classList.add('is-busy');
  try {
    await claimAccountReward(reward, token);
    trigger.classList.add('hidden');
    reward = null;
    close();
    await onClaimed?.();
  } catch (error) {
    const message = String(error?.payload?.message || error?.message || error);
    showError(
      /verify your email/i.test(message)
        ? 'Verify your email first, then come back to claim your Monthly Boost.'
        : message
    );
    turnstileHost.classList.remove('is-busy');
    if (widget != null) window.turnstile.reset(widget);
  }
}

function removeWidget() {
  if (widget != null && window.turnstile) {
    try {
      window.turnstile.remove(widget);
    } catch {
      /* already removed */
    }
  }
  widget = null;
  turnstileHost?.classList.remove('is-busy');
}

function clearError() {
  errorEl.textContent = '';
  errorEl.classList.add('hidden');
}

function showError(message) {
  errorEl.textContent = message;
  errorEl.classList.remove('hidden');
}
